import type { ListResponse, ProductStoreEntry } from '@data/utils/types';

type Entry = { key: string; value: string };
/** `since` is the version a change was made on, so the API can refuse one made over a colleague's newer save. */
type Stamped = { key: string; since?: string };
export type ProductStoreWrite = { entries: (Entry & Stamped)[]; removed?: Stamped[] };
/** The API, handed in by whoever loads a product, so a pure module's test never reaches the HTTP client. */
export interface ProductStoreApi {
  fetch: (productId: string) => Promise<ListResponse<ProductStoreEntry>>;
  /** Absent for a reader who may not write, a client: their page keeps its edits on screen and sends none. */
  write?: (productId: string, body: ProductStoreWrite) => Promise<ListResponse<ProductStoreEntry>>;
}

/** Every kind of value the money tabs keep for a product; the API accepts exactly these. */
const KINDS = [
  'productVersions',
  'productSow',
  'productSeniority',
  'productVersionTeam',
  'productServiceHours',
  'productPricing',
  'productCostMatrix',
  'productBudget',
  'productSprintDiscount',
  'productRetention',
  'productRetentionRelease',
  'productFractional',
  'productFractionalHours',
  'productQuarterCosts',
];
// These two lead with budget-or-spend, so the product id is their third segment rather than their second.
const KIND_FIRST = ['productFractionalHours', 'productQuarterCosts'];

/** The product a key belongs to, or null for a key the money tabs do not keep. */
export const productIdOfKey = (key: string): string | null => {
  const segments = key.split('@')[0].split(':');
  if (!KINDS.includes(segments[0])) return null;
  return segments[KIND_FIRST.includes(segments[0]) ? 2 : 1] ?? null;
};

const LEGACY_PREFIX = 'mock:';
// Under the API's ceiling on entries per request.
const UPLOAD_BATCH = 400;

const values = new Map<string, string>();
const stamps = new Map<string, string>();
const loaded = new Map<string, ProductStoreApi>();
const pending = new Map<string, { entries: Map<string, string>; removed: Set<string> }>();
const inFlight = new Map<string, Promise<void>>();
const sending = new Map<string, { entries: Map<string, string>; removed: Set<string> }>();
// Saves that land while a product is being read, which that read may have missed; null is a removal.
const landed = new Map<string, Map<string, { value: string | null; updatedAt?: string }>>();
const failureListeners = new Set<(productId: string) => void>();
// Whether each product's last send was refused, kept so a caller asking after it landed still hears it.
const lastSendRefused = new Map<string, boolean>();
const sendsStarted = new Map<string, number>();

const restamp = (productId: string, entries: ProductStoreEntry[]): void => {
  [...stamps.keys()].filter(key => productIdOfKey(key) === productId).forEach(key => stamps.delete(key));
  entries.forEach(entry => stamps.set(entry.key, entry.updatedAt));
};

const replaceProduct = (productId: string, entries: ProductStoreEntry[], api: ProductStoreApi): void => {
  [...values.keys()].filter(key => productIdOfKey(key) === productId).forEach(key => values.delete(key));
  entries.forEach(entry => values.set(entry.key, entry.value));
  restamp(productId, entries);
  // A save the read may have missed outlives it, or the screen would lose what the server holds.
  landed.get(productId)?.forEach(({ value, updatedAt }, key) => {
    if (value === null) values.delete(key);
    else values.set(key, value);
    if (updatedAt) stamps.set(key, updatedAt);
    else stamps.delete(key);
  });
  [sending.get(productId), pending.get(productId)].forEach(batch => {
    batch?.entries.forEach((value, key) => values.set(key, value));
    batch?.removed.forEach(key => values.delete(key));
  });
  // A read-only load never replaces a writable one: a page still editing would lose every save after it.
  if (api.write || !loaded.get(productId)?.write) loaded.set(productId, api);
};

const stamped = (key: string): Stamped => (stamps.has(key) ? { key, since: stamps.get(key) } : { key });

const send = async (productId: string): Promise<void> => {
  const batch = pending.get(productId);
  const api = loaded.get(productId);
  pending.delete(productId);
  if (!api?.write || !batch || (!batch.entries.size && !batch.removed.size)) return;
  sending.set(productId, batch);
  sendsStarted.set(productId, (sendsStarted.get(productId) ?? 0) + 1);
  try {
    const { items } = await api.write(productId, {
      entries: [...batch.entries].map(([key, value]) => ({ ...stamped(key), value })),
      removed: [...batch.removed].map(stamped),
    });
    // Only what this save wrote: a newer stamp on a key whose value is still the old one would pass a stale edit.
    const written = items.filter(item => batch.entries.has(item.key));
    written.forEach(item => stamps.set(item.key, item.updatedAt));
    batch.removed.forEach(key => stamps.delete(key));
    const reading = landed.get(productId);
    written.forEach(item => reading?.set(item.key, { value: item.value, updatedAt: item.updatedAt }));
    batch.removed.forEach(key => reading?.set(key, { value: null }));
    lastSendRefused.set(productId, false);
  } catch {
    lastSendRefused.set(productId, true);
    failureListeners.forEach(listener => listener(productId));
  } finally {
    sending.delete(productId);
  }
};

// One request per product per tick, and each waits for the last, so saves reach the server in order.
const schedule = (productId: string): void => {
  if (!loaded.has(productId) || inFlight.has(productId)) return;
  const run = Promise.resolve()
    .then(() => send(productId))
    .finally(() => {
      inFlight.delete(productId);
      if (pending.has(productId)) schedule(productId);
    });
  inFlight.set(productId, run);
};

const queue = (key: string): { entries: Map<string, string>; removed: Set<string> } | null => {
  const productId = productIdOfKey(key);
  // Dropped for a product that never loaded, or a later load would lay it over what the server holds, and
  // for a read-only one: a refused save reloads the page, which writes again, and round it goes.
  if (!productId || !loaded.get(productId)?.write) return null;
  const batch = pending.get(productId) ?? { entries: new Map(), removed: new Set() };
  pending.set(productId, batch);
  return batch;
};

/** Read and written like `localStorage`, so the tabs stay synchronous; a write reaches the API a tick later. */
export const productStore = {
  getItem: (key: string): string | null => values.get(key) ?? null,
  setItem: (key: string, value: string): void => {
    values.set(key, value);
    const batch = queue(key);
    if (!batch) return;
    batch.removed.delete(key);
    batch.entries.set(key, value);
    schedule(productIdOfKey(key)!);
  },
  removeItem: (key: string): void => {
    values.delete(key);
    const batch = queue(key);
    if (!batch) return;
    batch.entries.delete(key);
    batch.removed.add(key);
    schedule(productIdOfKey(key)!);
  },
  /** Forgets every product, loaded or not, as a sign-out does. */
  clear: (): void => {
    values.clear();
    stamps.clear();
    loaded.clear();
    pending.clear();
    sending.clear();
    landed.clear();
    lastSendRefused.clear();
    sendsStarted.clear();
  },
};

/** Called with the product whose save the API refused, so its page can reload what was really kept. */
export const onProductStoreSaveFailed = (listener: (productId: string) => void): (() => void) => {
  failureListeners.add(listener);
  return () => failureListeners.delete(listener);
};

/** Taken before writing, so `settleProductStore` can tell this caller's sends from an earlier refusal. */
export const productStoreMark = (productId: string): number => sendsStarted.get(productId) ?? 0;

/** Waits until everything queued for a product has been sent: false when the API refused the last of it. */
export const settleProductStore = async (productId: string, mark?: number): Promise<boolean> => {
  while (inFlight.has(productId)) {
    // eslint-disable-next-line no-await-in-loop -- each send is queued behind the last, so they are awaited in turn
    await inFlight.get(productId);
  }
  if (mark !== undefined && productStoreMark(productId) === mark) return true;
  return !lastSendRefused.get(productId);
};

const isJson = (value: string): boolean => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

// What this browser kept for a product before the API did.
const legacyKeys = (productId: string): string[] => {
  try {
    return Object.keys(localStorage).filter(
      key => key.startsWith(LEGACY_PREFIX) && productIdOfKey(key.slice(LEGACY_PREFIX.length)) === productId,
    );
  } catch {
    return [];
  }
};

// Once per browser: the copy is dropped after the upload, or a stale one would bring back what a colleague removed.
const uploadLegacy = async (
  productId: string,
  api: ProductStoreApi,
  items: ProductStoreEntry[],
): Promise<ProductStoreEntry[]> => {
  const held = legacyKeys(productId);
  if (!held.length || !api.write) return items;
  const onServer = new Set(items.map(item => item.key));
  const missing = held
    .map(key => ({ key: key.slice(LEGACY_PREFIX.length), value: localStorage.getItem(key) ?? '' }))
    .filter(entry => !onServer.has(entry.key) && isJson(entry.value));
  let kept = items;
  try {
    for (let at = 0; at < missing.length; at += UPLOAD_BATCH) {
      // eslint-disable-next-line no-await-in-loop -- in order, so the last answer holds every batch
      ({ items: kept } = await api.write!(productId, { entries: missing.slice(at, at + UPLOAD_BATCH) }));
    }
    held.forEach(key => localStorage.removeItem(key));
  } catch {
    // Kept for the next opening, and the page opens on what the API holds.
  }
  return kept;
};

const load = async (productId: string, api: ProductStoreApi): Promise<number> => {
  // After the saves already sent, so the read holds them, the way each save waits for the last.
  // eslint-disable-next-line no-await-in-loop -- a save can be queued behind the one being waited on
  while (inFlight.has(productId)) await inFlight.get(productId);
  landed.set(productId, new Map());
  try {
    const { items } = await api.fetch(productId);
    const kept = await uploadLegacy(productId, api, items);
    replaceProduct(productId, kept, api);
    return kept.length;
  } finally {
    landed.delete(productId);
  }
};

const loading = new Map<string, Promise<number>>();

/** Loads a product's values from the API, first uploading whatever of this browser's older copy it lacks. */
export const loadProductStore = (productId: string, api: ProductStoreApi): Promise<number> => {
  // Shared while in flight: a second answer landing after the page has written would put the old values back.
  // Keyed on whether it can write too, or a read-only load in flight would hand our page an api that drops saves.
  const key = `${productId}:${api.write ? 'write' : 'read'}`;
  const running = loading.get(key) ?? load(productId, api).finally(() => loading.delete(key));
  loading.set(key, running);
  return running;
};
